/**
 * Choose Path — Competitor vs Casual onboarding tier selection.
 * Shown after handle selection, before quick-profile.
 */
import { useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ActivityIndicator, ScrollView, Pressable } from 'react-native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { ChevronLeft } from 'lucide-react-native';
import { useRouter } from 'expo-router';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { TYPO } from '@/constants/typography';
import { RADIUS, FONT, SPACE } from '@/constants/designTokens';
import OnboardingProgress from '@/components/OnboardingProgress';
import { setOnboardingTier, TIER_CONFIGS, type OnboardingTier } from '@/lib/onboardingTiers';

export default function ChoosePath() {
  const router = useRouter();
  const { theme } = useTheme();
  const c = theme.colors;
  const { profile } = useAuth();
  const [selected, setSelected] = useState<OnboardingTier | null>(null);
  const [saving, setSaving] = useState(false);

  const handleContinue = () => {
    if (!selected || !profile?.id) return;
    // Navigate immediately — don't block on Supabase
    router.push('/(onboarding)/quick-profile');
    // Save tier in background (non-blocking)
    setOnboardingTier(profile.id, selected).catch(() => {});
  };

  // Allow tapping card to immediately proceed without needing a separate continue button
  const handleCardSelect = async (tier: OnboardingTier) => {
    setSelected(tier);
  };

  const tiers: OnboardingTier[] = ['competitor', 'casual'];

  const insets = useSafeAreaInsets();

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: c.background }]} edges={['top']}>
      <TouchableOpacity style={[styles.backBtn, { top: insets.top + 8 }]} onPress={() => router.back()}>
        <ChevronLeft size={24} color={c.text} />
      </TouchableOpacity>
      <OnboardingProgress step={3} />

      <ScrollView contentContainerStyle={styles.scrollContent} keyboardShouldPersistTaps="handled">
        <Text style={[styles.title, { color: c.text, fontFamily: TYPO.heading.bold }]}>
          HOW DO YOU WANT TO USE DUEL?
        </Text>
        <Text style={[styles.subtitle, { color: c.textSecondary }]}>
          You can change this anytime in settings.
        </Text>

        <View style={styles.options}>
          {tiers.map((tier) => {
            const config = TIER_CONFIGS[tier];
            const isSelected = selected === tier;
            return (
              <Pressable
                key={tier}
                style={({ pressed }) => [
                  styles.optionCard,
                  {
                    backgroundColor: isSelected ? c.accent + '15' : c.surface,
                    borderColor: isSelected ? c.accent : c.border,
                    borderWidth: isSelected ? 2 : 1,
                    opacity: pressed ? 0.85 : 1,
                  },
                ]}
                onPress={() => handleCardSelect(tier)}
                android_ripple={{ color: c.accent + '20', borderless: false }}
                accessibilityRole="button"
              >
                <Text style={styles.emoji}>{config.emoji}</Text>
                <Text style={[styles.optionLabel, { color: isSelected ? c.accent : c.text, fontFamily: TYPO.heading.bold }]}>
                  {config.label}
                </Text>
                <Text style={[styles.optionDesc, { color: c.textSecondary }]}>
                  {config.description}
                </Text>
              </Pressable>
            );
          })}
        </View>

        <Pressable
          style={({ pressed }) => [
            styles.continueBtn,
            {
              backgroundColor: selected ? c.accent : c.surfaceVariant,
              marginTop: SPACE.lg,
              marginBottom: insets.bottom + 24,
              opacity: pressed ? 0.85 : 1,
            }
          ]}
          onPress={handleContinue}
          disabled={!selected || saving}
          hitSlop={{ top: 12, bottom: 12, left: 0, right: 0 }}
          android_ripple={{ color: 'rgba(0,0,0,0.15)', borderless: false }}
          accessibilityRole="button"
        >
          {saving ? (
            <ActivityIndicator color="#000" />
          ) : (
            <Text style={[styles.continueBtnText, { color: selected ? '#000' : c.textTertiary, fontFamily: TYPO.heading.bold }]}>
              {selected ? TIER_CONFIGS[selected].ctaText : 'CHOOSE YOUR PATH'}
            </Text>
          )}
        </Pressable>
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  backBtn: { position: 'absolute', left: 16, zIndex: 10, padding: 8 },
  scrollContent: { padding: SPACE.lg, gap: SPACE.lg },
  title: { fontSize: FONT.xl, textAlign: 'center', letterSpacing: 1 },
  subtitle: { fontSize: FONT.sm, textAlign: 'center' },
  options: { gap: SPACE.md },
  optionCard: {
    padding: 24,
    borderRadius: RADIUS.xs,
    alignItems: 'center',
    gap: SPACE.sm,
  },
  emoji: { fontSize: 36 },
  optionLabel: { fontSize: FONT.lg, letterSpacing: 1.5 },
  optionDesc: { fontSize: FONT.sm, textAlign: 'center', lineHeight: 20 },
  continueBtn: {
    paddingVertical: 16,
    alignItems: 'center',
    borderRadius: RADIUS.xs,
  },
  continueBtnText: {
    fontSize: FONT.base,
    letterSpacing: 1,
  },
});
